import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

// Default settings
const defaultSettings = {
    // High Value Lead Criteria
    highValueCriteria: {
        minLeadValue: 5000,
        serviceTypes: ['onsite', 'premium'],
        responseTimeThreshold: 15, // minutes
        enableAutoEscalation: true,
        priorityKeywords: ['urgent', 'asap', 'emergency', 'premium'],
        minPageViews: 5,
        highValueSuburbs: ['Toorak', 'South Yarra', 'Brighton'],
        enableMultipleProducts: true
    },
    // Kanban Board Settings
    kanbanSettings: {
        thresholds: {
            'Online Quotes': 2,
            'Onsite Quotes': 3,
            'Price Estimates': 1,
            'Drop Off': 7
        },
        enableThresholdAlerts: true,
        highlightOverThreshold: true
    },
    // Timing Thresholds
    timingThresholds: {
        followUpWindow: 24, // hours
        abandonmentThreshold: 72, // hours
        responseTimeGoal: 30, // minutes
        escalationTime: 60, // minutes
        businessHoursStart: '09:00',
        businessHoursEnd: '17:00'
    },
    // Notifications
    notifications: {
        emailAlerts: true,
        smsAlerts: false,
        desktopNotifications: true,
        highValueLeadAlert: true,
        deadlineReminders: true,
        dailyDigest: true
    },
    // Dashboard Settings
    dashboard: {
        autoRefreshInterval: 30, // seconds
        defaultDateRange: 30, // days
        showPieChart: true,
        showGradients: true,
        compactView: false
    },
    // Integration Settings
    integration: {
        twilioEnabled: false,
        twilioAccountSid: '',
        twilioAuthToken: '',
        webhookUrl: '',
        autoSync: true,
        syncInterval: 300 // seconds
    }
};

// Create context
const SettingsContext = createContext();

// Settings provider component
export const SettingsProvider = ({ children }) => {
    const [settings, setSettings] = useState(defaultSettings);
    const [isLoading, setIsLoading] = useState(true);

    // Load settings from localStorage and backend on component mount
    useEffect(() => {
        const loadSettings = async () => {
            try {
                // Load from localStorage first
                const savedSettings = localStorage.getItem('eclipseDashSettings');
                let localSettings = defaultSettings;
                if (savedSettings) {
                    const parsedSettings = JSON.parse(savedSettings);
                    // Merge with defaults to ensure all settings exist
                    localSettings = { ...defaultSettings, ...parsedSettings };
                }

                // Load high-value criteria from backend
                try {
                    const response = await axios.get('/api/leads/config/highvalue');
                    if (response.data) {
                        localSettings = {
                            ...localSettings,
                            highValueCriteria: {
                                ...localSettings.highValueCriteria,
                                ...response.data
                            }
                        };
                    }
                } catch (backendError) {
                    console.warn('Failed to load high-value criteria from backend:', backendError);
                    // Continue with local settings if backend fails
                }

                setSettings(localSettings);
            } catch (error) {
                console.error('Failed to load settings:', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadSettings();
    }, []);

    // Save settings to localStorage whenever settings change
    useEffect(() => {
        if (!isLoading) {
            try {
                localStorage.setItem('eclipseDashSettings', JSON.stringify(settings));
            } catch (error) {
                console.error('Failed to save settings to localStorage:', error);
            }
        }
    }, [settings, isLoading]);

    // Update a specific setting category and field
    const updateSetting = (category, field, value) => {
        setSettings(prev => ({
            ...prev,
            [category]: {
                ...prev[category],
                [field]: value
            }
        }));
    };

    // Update multiple settings at once
    const updateSettings = (newSettings) => {
        setSettings(prev => ({
            ...prev,
            ...newSettings
        }));
    };

    // Reset settings to defaults
    const resetSettings = () => {
        setSettings(defaultSettings);
    };

    // Get high-value lead criteria for a lead
    const isHighValueLead = (lead) => {
        const criteria = settings.highValueCriteria;

        // Check page views
        if (lead.page_view_count >= criteria.minPageViews) {
            return true;
        }

        // Check suburb
        if (criteria.highValueSuburbs.some(suburb =>
            lead.suburb && lead.suburb.toLowerCase().includes(suburb.toLowerCase())
        )) {
            return true;
        }

        // Check multiple products
        if (criteria.enableMultipleProducts && lead.Product && lead.Product.includes(',')) {
            return true;
        }

        // Check priority keywords in service type or notes
        const textToCheck = [
            lead.service_type,
            lead.product,
            lead.qualification_notes
        ].filter(Boolean).join(' ').toLowerCase();

        if (criteria.priorityKeywords.some(keyword =>
            textToCheck.includes(keyword.toLowerCase())
        )) {
            return true;
        }

        return false;
    };

    // Get threshold for a specific status
    const getThresholdForStatus = (status) => {
        return settings.kanbanSettings.thresholds[status] || 0;
    };

    // Check if a lead is over threshold
    const isLeadOverThreshold = (lead) => {
        if (!settings.kanbanSettings.enableThresholdAlerts) {
            return false;
        }

        const threshold = getThresholdForStatus(lead.status);
        const daysInStatus = lead.daysInStatus || 0;

        return daysInStatus > threshold && threshold > 0;
    };

    const value = {
        settings,
        updateSetting,
        updateSettings,
        resetSettings,
        isHighValueLead,
        getThresholdForStatus,
        isLeadOverThreshold,
        isLoading
    };

    return (
        <SettingsContext.Provider value={value}>
            {children}
        </SettingsContext.Provider>
    );
};

// Custom hook to use settings context
export const useSettings = () => {
    const context = useContext(SettingsContext);
    if (!context) {
        throw new Error('useSettings must be used within a SettingsProvider');
    }
    return context;
};

export default SettingsContext;
